import { NotificationsContext } from "@ui-components/Notifications";
import {useContext, useState} from "react";
import useAsyncError from "@hooks/useAsyncError";
import {useIntl} from "react-intl";
import useCountryAxiosInstance from "@hooks/useCountryAxiosInstance";
import {buildPayloadAsdea} from "../lib/connectors/utils";
import {downloadBlob} from "../../commons/lib/utils";
import moment from "moment";
import {ConnectorResponses} from "../../services/connettori/types";
import {NORMATIVA_OPTIONS} from "../../services/connettori/custom-input-geometria/lib/consts";
import {SYSTEM_OPTIONS, TENSILE_CURB_OPTIONS} from "../../services/connettori/custom-input-rinforzo/lib/consts";
import _ from "lodash";
import {LogoSchema} from "@feature/profile-logo-editor/types";
import {useLogo} from "@feature/profile-logo-editor/hooks/useLogo";

export const useConnectorsResults = () => {

  // context
  const intl = useIntl();
  const {push} = useContext(NotificationsContext);

  // hooks
  const countryApiRequest = useCountryAxiosInstance();
  const throwError = useAsyncError();
  const {getLogo} = useLogo();

  // state
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // handlers
  const recompute = async (
    responses:
      ConnectorResponses  // diaphragm schema
      | {[key: string]: number},  // vertical/wood connectors schema
    surveyId?: number,
    type: "diaphragm" | "vertical" | "wood" = "diaphragm"
  ) => {
    setLoading(true);
    try {
      return await countryApiRequest!.post(
        "/connectors",
        {
          survey_id: surveyId,
          type_: type,
          data: {
            diaphragm: type === "diaphragm" ? buildPayloadAsdea(responses as ConnectorResponses) : null,
            vertical: type === "vertical" ? responses : null,
            wood: type === "wood" ? responses : null,
          },
        },
      );
    }
    catch (e: any) {
      const type = e.response?.status % 400 < 100 ? "warning" : "error";
      let title, text;
      switch (e.response?.status) {
        case 400:
          title = intl.formatMessage({id: "connectors_computation_error", defaultMessage: "Errore inatteso durante il calcolo dei risultati"});
          text = intl.formatMessage({id: "connectors_contact_technical_support_cta", defaultMessage: "Contattare il supporto tecnico"});
          break;
        case 401:
          title = intl.formatMessage({id: "connectors_authentication_error", defaultMessage: "Errore di autenticazione"});
          text = intl.formatMessage({id: "connectors_contact_technical_support_cta", defaultMessage: "Contattare il supporto tecnico"});
          break;
        case 422: {
          const fields: string[] | undefined = e.response.data?.detail
            ?.map(({loc, msg}: { loc: string[], msg: string }) => {
              const path = loc.filter((s) => s !== "__root__").join(".");
              return <><span className="font-medium">{path}</span> - {_.capitalize(msg)}</>
            });
          title = intl.formatMessage({
            id: "connectors_validation_error",
            defaultMessage: "I dati inseriti sono inconsistenti"
          });
          text = <>
            <p>
              {intl.formatMessage({
                id: "connectors_validation_error_message", defaultMessage: "Controlla i seguenti campi"
              })}:
            </p>
            <ul className="list-disc list-inside">
              {fields?.map((s, idx) => <li key={idx}>{s}</li>)}
            </ul>
          </>;
          break;
        }
        default:
          title = intl.formatMessage({id: "server_error"});
          break;
      }
      push({type, title, text});
    } finally {
      setLoading(false);
    }
  }
  const download = async (
    inputObj: {[key: string]: any},
    outputObj: {[key: string]: any[]},
    type: "diaphragm" | "vertical" | "wood" = "diaphragm",
    returnBlob: boolean = false,
    surveyName: string | null | undefined = null,
  ) => {
    setDownloading(true);
    const logo: LogoSchema = await getLogo();
    try {
      const payload = {
        type_: type,
        data: {
          diaphragm: type === "diaphragm"
            ? {
              ...outputObj,
              FromConfiguratorInput: {
                existing_masonry: inputObj.geometria.type,
                computation_method: NORMATIVA_OPTIONS[inputObj.geometria.normativa],
                tension_curb_choice: TENSILE_CURB_OPTIONS[inputObj.rinforzo.tipologia_cordolo],
                tension_curb_type_choice: !!inputObj.rinforzo.sistema_cordolo
                  ? SYSTEM_OPTIONS[inputObj.rinforzo.sistema_cordolo]
                  : "Barre B450C",
              },
              ...(logo?.url ? {logo_url: logo.url} : {}),
            }
            : null,
          ...(["vertical", "wood"].reduce(
            (acc, curr) => ({
              ...acc,
              [curr]: type === curr
                ? {...outputObj, ..._.pick(inputObj, ["FromConfiguratorInput"]), ...(logo?.url ? {logo_url: logo.url} : {})}
                : null,
            }),
            {}
          ))
        },
        survey_name: surveyName ?? null,
      }
      const blob = await countryApiRequest!.post(
        "/service_surveys/connectors-pdf",
        payload,
        {responseType: "blob"}
      ) as Blob;
      if (returnBlob)
        return blob;
      downloadBlob(blob, `${moment().format("YYYYMMDDHHmmss")}-report-connectors.pdf`);
    }
    catch (e: any) {
      if (e.response?.status === 422) {
        if (returnBlob) {
          return null;
        } else {
          push({
            type: "warning",
            title: intl.formatMessage({
              id: "design_survey_download_unprocessable_warning_title",
              defaultMessage: "Impossibile scaricare il report",
            }),
            text: intl.formatMessage({
              id: "design_survey_download_unprocessable_warning_message",
              defaultMessage: "Controlla che tutti i campi siano stati compilati correttamente e riprova",
            }),
          });
        }
      }
      else {
        push({
          type: "error",
          title: intl.formatMessage({id: "server_error"}),
          text: "CONNECTORS_EXPORT_ERROR"
        });
        throwError(e);
      }
    }
    finally {
      setDownloading(false);
    }
  }
  const override = async (
    surveyId: number,
    type: "diaphragm" | "vertical" | "wood",
    payload: any
  ) => {
    try {
      await countryApiRequest!.put(`/connectors/${surveyId}/${type}`, payload);
    }
    catch (e: any) {
      push({
        type: "error",
        title: intl.formatMessage({id: "server_error"}),
        text: "CONNECTORS_OVERRIDE_ERROR"
      });
      throwError(e);
    }
  }

  return {recompute, download, override, loading, downloading};
}